import { DocumentItemsValidationsBoPart, propertyChangedJoinPointFilter, QuantityLessThanZeroError, QuantityMustBeGreaterThanZeroError } from '@skylab-shared/business';
import { BusinessObjectPartContext, ModelBase, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { CalculationMethod, InventoryDocumentItemModel, InventoryDocumentKind } from '@skylab-shared/dataModel';
import { InventoryDocumentItemPriceCannotBeLessThanZero } from '../violations/inventory-document-item-price-cannot-be-less-than-zero-error';


export interface InventoryDocumentItemsValidationsBoPartModelPart extends ModelBase {
    calculationMethod: CalculationMethod;
    issuedByVatPayer: boolean;
    kind: InventoryDocumentKind;
}

export class InventoryDocumentItemsValidationsBoPart extends DocumentItemsValidationsBoPart<InventoryDocumentItemsValidationsBoPartModelPart> {
    public constructor(
        weaver: Weaver,
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<InventoryDocumentItemsValidationsBoPartModelPart>): Promise<void> {
        await super.initializeAsync(context);

        this.registerPriceValidationRules();
        this.registerQuantityValidationRules();
        this.registerReadonlyRules();
    }

    protected registerReadonlyRules(): void {
        this.engine.readonly.setAsReadonly([InventoryDocumentItemModel, 'value_vatAfterDiscount']);
        this.engine.readonly.setAsReadonly([InventoryDocumentItemModel, 'inventoryUnitCost']);
        this.engine.readonly.setAsReadonly([InventoryDocumentItemModel, 'inventoryTotalCost']);
    }

    protected override registerReadonlyOmitRules(): void {
        super.registerReadonlyOmitRules();
        this.engine.readonlyChecker.addOmitRule([
            [InventoryDocumentItemModel, 'inventoryUnitCost'],
            [InventoryDocumentItemModel, 'inventoryTotalCost']
        ]);
    }

    private registerPriceValidationRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['calculationMethod'])(
            () => {
                this.engine.validation.queueValidation([InventoryDocumentItemModel, 'price_grossBeforeDiscount']);
                this.engine.validation.queueValidation([InventoryDocumentItemModel, 'price_netBeforeDiscount']);
            }
        )));

        this.engine.validation.addRule(
            [[InventoryDocumentItemModel, 'price_grossBeforeDiscount']],
            [[InventoryDocumentItemModel, 'price_grossBeforeDiscount']],
            ctx => {
                if (this.bo.model.calculationMethod === CalculationMethod.Net) return true;
                return ctx.source.price_grossBeforeDiscount >= 0;
            },
            InventoryDocumentItemPriceCannotBeLessThanZero
        );

        this.engine.validation.addRule(
            [[InventoryDocumentItemModel, 'price_netBeforeDiscount']],
            ctx => {
                if (this.bo.model.calculationMethod === CalculationMethod.Gross) return true;
                return ctx.source.price_netBeforeDiscount >= 0;
            },
            InventoryDocumentItemPriceCannotBeLessThanZero
        );
    }

    private registerQuantityValidationRules(): void {
        this.engine.validation.addRule(
            [[InventoryDocumentItemModel, 'quantity']],
            ctx => !ctx.source.adjustedItemId ? ctx.source.quantity > 0 : true,
            QuantityMustBeGreaterThanZeroError
        );

        this.engine.validation.addRule(
            [[InventoryDocumentItemModel, 'quantity']],
            ctx => ctx.source.adjustedItemId ? ctx.source.quantity >= 0 : true,
            QuantityLessThanZeroError
        );
    }
}
